import Web3 from 'web3';

// Network configuration (duplicated to avoid circular imports)
const NETWORK = process.env.REACT_APP_NETWORK || 'mordor';
const RPC_URLS = {
  MORDOR: process.env.REACT_APP_MORDOR_RPC_URL || 'https://rpc.mordor.etccooperative.org',
  MAINNET: process.env.REACT_APP_MAINNET_RPC_URL || 'https://etc.rivet.link',
};
const getCurrentRpcUrl = () => {
  return NETWORK === 'mainnet' ? RPC_URLS.MAINNET : RPC_URLS.MORDOR;
};

// Create web3 instance - prefers wallet provider, falls back to configured RPC
const getRpcUrl = () => {
  // If wallet provider is available, use it (wallet will handle RPC)
  if (Web3.givenProvider) {
    return Web3.givenProvider;
  }
  // Otherwise use configured RPC URL based on network
  return getCurrentRpcUrl();
};

// Create web3 instance - will use wallet provider if available, otherwise configured RPC
export const web3 = new Web3(getRpcUrl());

// Function to recreate web3 instance if RPC changes (for network switching)
export const recreateWeb3Instance = () => {
  return new Web3(getRpcUrl());
};

export const formatAddress = (address) => {
  if (!address) return '';
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
};

export const convertWeiToEther = (wei) => {
  if (!wei) return '0';
  return web3.utils.fromWei(wei, 'ether');
};

export const convertEtherToWei = (ether) => {
  if (!ether) return '0';
  return web3.utils.toWei(ether.toString(), 'ether');
};

export const isValidAddress = (address) => {
  return web3.utils.isAddress(address);
};

export const getContractInstance = (abi, address) => {
  return new web3.eth.Contract(abi, address);
};

// Contract addresses (duplicated to avoid circular imports)
const CONTRACT_ADDRESSES = {
  ETCMCLICENSEV2: process.env.REACT_APP_ETCMCLICENSEV2_ADDRESS || '0x8f09025e5d0a100918E7C259199F3a3C5D2091C9',
  ETCPOW: process.env.REACT_APP_ETCPOW_ADDRESS || '0x6856FE43e43996830526cEd56BbC53f3F6a8E80E',
};
const MAINNET_CONTRACT_ADDRESSES = {
  ETCMCLICENSEV2: process.env.REACT_APP_MAINNET_ETCMCLICENSEV2_ADDRESS || '',
  ETCPOW: process.env.REACT_APP_MAINNET_ETCPOW_ADDRESS || '',
};
const getContractAddress = (contractName) => {
  // If on mainnet and mainnet address is configured, use it
  if (NETWORK === 'mainnet') {
    const mainnetAddress = MAINNET_CONTRACT_ADDRESSES[contractName];
    if (mainnetAddress && mainnetAddress.trim() !== '') {
      return mainnetAddress;
    }
  }
  // Otherwise use the default (Mordor) address
  return CONTRACT_ADDRESSES[contractName];
};

export const getETCMCLicenseContract = (abi) => {
  const address = getContractAddress('ETCMCLICENSEV2') || CONTRACT_ADDRESSES.ETCMCLICENSEV2;
  return getContractInstance(abi, address);
};

export const getETCPOWContract = (abi) => {
  const address = getContractAddress('ETCPOW') || CONTRACT_ADDRESSES.ETCPOW;
  return getContractInstance(abi, address);
};

/**
 * Wait for a transaction to be confirmed
 * @param {string} txHash - Transaction hash
 * @param {number} confirmations - Number of confirmations to wait for (default: 1)
 * @param {number} timeout - Timeout in milliseconds (default: 120000 = 2 minutes)
 * @returns {Promise<object>} Transaction receipt
 */
export const waitForTransaction = async (txHash, confirmations = 1, timeout = 180000) => {
  return new Promise((resolve, reject) => {
    const startTime = Date.now();
    let checkCount = 0;
    
    const checkTransaction = async () => {
      try {
        // Check if timeout exceeded
        if (Date.now() - startTime > timeout) {
          reject(new Error('Transaction confirmation timeout. The transaction may still be pending.'));
          return;
        }

        try {
          const receipt = await web3.eth.getTransactionReceipt(txHash);
          
          if (receipt) {
            // Transaction was mined, check confirmations
            const currentBlock = await web3.eth.getBlockNumber();
            const confirmationsCount = currentBlock - receipt.blockNumber + 1;
            
            if (confirmationsCount >= confirmations) {
              resolve(receipt);
            } else {
              // Wait a bit and check again for more confirmations
              setTimeout(checkTransaction, 3000);
            }
          } else {
            // Receipt is null - transaction not mined yet, keep checking
            checkCount++;
            const delay = checkCount < 5 ? 5000 : 3000; // Longer delay for first few checks
            setTimeout(checkTransaction, delay);
          }
        } catch (receiptError) {
          // Error getting receipt - check if it's a "not found" error
          const errorMsg = (receiptError.message || '').toLowerCase();
          const errorName = (receiptError.name || '').toLowerCase();
          const errorString = JSON.stringify(receiptError).toLowerCase();
          
          // "Transaction not found" errors are normal right after sending - keep checking
          // Check multiple ways the error might be expressed
          const isNotFoundError = 
            errorMsg.includes('not found') || 
            errorMsg.includes('transactionnotfound') ||
            errorName === 'transactionnotfound' ||
            errorString.includes('transactionnotfound') ||
            (errorMsg.includes('transaction') && errorMsg.includes('not found')) ||
            (receiptError.code && receiptError.code === -32000); // Some RPCs use this code for "not found"
          
          if (isNotFoundError) {
            checkCount++;
            // Give more time on first few checks (ETC Classic can be slow)
            const delay = checkCount < 5 ? 5000 : 3000;
            setTimeout(checkTransaction, delay);
          } else {
            // Some other error - might be a real problem, but give it a few more tries
            checkCount++;
            if (checkCount < 15) {
              // Retry more times for other errors too (might be temporary RPC issues)
              setTimeout(checkTransaction, 3000);
            } else {
              // After many retries, reject
              reject(receiptError);
            }
          }
        }
      } catch (error) {
        // Catch-all for any other errors
        const errorMsg = (error.message || '').toLowerCase();
        const errorName = (error.name || '').toLowerCase();
        const errorString = JSON.stringify(error).toLowerCase();
        
        const isNotFoundError = 
          errorMsg.includes('not found') || 
          errorMsg.includes('transactionnotfound') ||
          errorName === 'transactionnotfound' ||
          errorString.includes('transactionnotfound') ||
          (errorMsg.includes('transaction') && errorMsg.includes('not found'));
        
        if (isNotFoundError) {
          // Keep checking
          checkCount++;
          const delay = checkCount < 5 ? 5000 : 3000;
          setTimeout(checkTransaction, delay);
        } else {
          // Unknown error - retry more times (might be temporary RPC issues)
          checkCount++;
          if (checkCount < 15) {
            setTimeout(checkTransaction, 3000);
          } else {
            reject(error);
          }
        }
      }
    };
    
    // Wait a bit before first check (transaction needs time to appear in mempool)
    // ETC Classic can be slower than Ethereum
    setTimeout(checkTransaction, 3000);
  });
};

/**
 * Check if error is a user rejection
 * @param {Error} error - Error object
 * @returns {boolean}
 */
export const isUserRejection = (error) => {
  if (!error) return false;
  const code = error.code;
  const message = (error.message || '').toLowerCase();
  
  return (
    code === 4001 ||
    code === 'ACTION_REJECTED' ||
    message.includes('user rejected') ||
    message.includes('user denied') ||
    message.includes('rejected the request') ||
    message.includes('denied transaction signature')
  );
};
